import type { AdvisorAgreement } from '@src/components/sco/Advisors/types/advisorAgreement'
import {
  AGREEMENT_STATUS_DECLINED,
  AGREEMENT_STATUS_DRAFT,
  AGREEMENT_STATUS_SENT,
  AGREEMENT_STATUS_SIGNED,
  advisorDisplayName,
  formatAgreementDate,
  formatAgreementSigners,
  formatEquityPercent,
} from '@src/components/sco/Advisors/utils/agreementKeys'
import { canDeleteUnsignedAdvisorAgreement } from '@src/components/sco/Advisors/utils/agreementSend'

export type AgreementPartyRole = 'FOUNDER' | 'ADVISOR'

export const ADVISOR_AGREEMENTS_PAGE_SIZE = 100

export const advisorAgreementsListVariables = (role: AgreementPartyRole, page = 1) => ({
  role,
  pageSize: ADVISOR_AGREEMENTS_PAGE_SIZE,
  page,
})

const ROW_PALETTES = ['purple', 'orange', 'pink', 'yellow', 'teal', 'blue'] as const

export type AgreementListItem = {
  advisorAgreementId: string
  name: string
  sub: string
  version: string
  date: string
  palette: string
  statusKey: string
  statusTitle?: string
  pictureUrl?: string | null
  documensoDocumentId?: string | null
  partyRole: AgreementPartyRole
  signersLine?: string | null
  canEdit?: boolean
  canDelete?: boolean
}

export type AgreementPrimaryActionKind = 'sign' | 'edit' | 'view' | 'pdf'

export type AgreementPrimaryAction = {
  kind: AgreementPrimaryActionKind
  label: string
}

export type AgreementMenuActionKind = 'pdf' | 'checkStatus' | 'requestChanges' | 'decline' | 'delete'

export type AgreementMenuGroupKey = 'document' | 'respond' | 'danger'

export type AgreementMenuItem = {
  kind: AgreementMenuActionKind
  label: string
}

export type AgreementMenuGroup = {
  key: AgreementMenuGroupKey
  label: string
  items: AgreementMenuItem[]
}

export type AgreementStatusGroupKey = 'draft' | 'sent' | 'signed' | 'declined' | 'other'

export type AgreementStatusGroup = {
  key: AgreementStatusGroupKey
  label: string
  color: string
  rows: AgreementListItem[]
}

const KNOWN_STATUS_KEYS = new Set([
  AGREEMENT_STATUS_DRAFT,
  AGREEMENT_STATUS_SENT,
  AGREEMENT_STATUS_SIGNED,
  AGREEMENT_STATUS_DECLINED,
])

const dateSourceForAgreement = (agreement: AdvisorAgreement): string | null | undefined => {
  if (agreement.statusKey === AGREEMENT_STATUS_SIGNED) {
    return agreement.signedAt || agreement.updatedAt
  }
  if (agreement.statusKey === AGREEMENT_STATUS_SENT) {
    return agreement.sentAt || agreement.updatedAt
  }
  return agreement.updatedAt || agreement.createdAt
}

export const mergeAdvisorAgreementItems = (
  founderItems: Array<AdvisorAgreement | null | undefined>,
  advisorItems: Array<AdvisorAgreement | null | undefined>
): Array<{ agreement: AdvisorAgreement; partyRole: AgreementPartyRole }> => {
  const seen = new Set<string>()
  const merged: Array<{
    agreement: AdvisorAgreement
    partyRole: AgreementPartyRole
    sortAt: number
  }> = []

  const push = (item: AdvisorAgreement | null | undefined, partyRole: AgreementPartyRole) => {
    const id = item?.advisorAgreementId
    if (!item || !id || seen.has(id)) {
      return
    }
    seen.add(id)
    const stamp = Date.parse(String(dateSourceForAgreement(item) || ''))
    merged.push({
      agreement: item,
      partyRole,
      sortAt: Number.isNaN(stamp) ? 0 : stamp,
    })
  }

  founderItems.forEach(item => push(item, 'FOUNDER'))
  advisorItems.forEach(item => push(item, 'ADVISOR'))

  merged.sort((a, b) => b.sortAt - a.sortAt)
  return merged.map(({ agreement, partyRole }) => ({ agreement, partyRole }))
}

export const toAgreementListItem = (
  agreement: AdvisorAgreement,
  partyRole: AgreementPartyRole,
  index: number
): AgreementListItem | null => {
  const id = agreement.advisorAgreementId
  if (!id) {
    return null
  }

  const tier = agreement.contributionTier?.title || agreement.contributionTierKey || 'Advisor'
  const equity = formatEquityPercent(agreement.equityPercent)
  const vest = agreement.vestingMonths != null ? `${agreement.vestingMonths}mo vest` : null
  const cliff = agreement.cliffMonths != null ? `${agreement.cliffMonths}mo cliff` : null
  const subParts = [tier, equity, [vest, cliff].filter(Boolean).join(', ')].filter(
    part => part && part !== '—'
  )

  const name =
    partyRole === 'ADVISOR'
      ? agreement.founderName ||
        agreement.founderUser?.fullName ||
        agreement.founderUser?.displayName ||
        agreement.company?.name ||
        'Founder'
      : advisorDisplayName(agreement)

  const pictureUrl =
    partyRole === 'ADVISOR' ? agreement.founderUser?.pictureUrl : agreement.advisorUser?.pictureUrl

  return {
    advisorAgreementId: id,
    name,
    sub: subParts.join(' · ') || (partyRole === 'ADVISOR' ? 'Advisory terms' : 'Draft terms'),
    version: `Version ${agreement.version ?? 1}${agreement.fastTrackApplied ? ' · FAST' : ''}`,
    date: formatAgreementDate(dateSourceForAgreement(agreement)),
    palette: ROW_PALETTES[index % ROW_PALETTES.length],
    statusKey: agreement.statusKey || AGREEMENT_STATUS_DRAFT,
    statusTitle: agreement.status?.title || undefined,
    pictureUrl,
    documensoDocumentId: agreement.documensoDocumentId || null,
    partyRole,
    signersLine:
      (agreement.statusKey || AGREEMENT_STATUS_DRAFT) === AGREEMENT_STATUS_SENT
        ? formatAgreementSigners({
            founderName: agreement.founderUser?.fullName || agreement.founderUser?.displayName || 'Founder',
            advisorName: advisorDisplayName(agreement),
            founderHasSigned: agreement.founderHasSigned,
            advisorHasSigned: agreement.advisorHasSigned,
          })
        : null,
    canEdit: Boolean(agreement.currentUserCanEdit),
    canDelete: canDeleteUnsignedAdvisorAgreement({
      currentUserRole: partyRole === 'FOUNDER' ? 'founder' : 'advisor',
      statusKey: agreement.statusKey || AGREEMENT_STATUS_DRAFT,
      currentUserCanDelete: agreement.currentUserCanDelete,
    }),
  }
}

export const toMergedAgreementListItems = (
  founderItems: Array<AdvisorAgreement | null | undefined>,
  advisorItems: Array<AdvisorAgreement | null | undefined>
): AgreementListItem[] =>
  mergeAdvisorAgreementItems(founderItems, advisorItems)
    .map(({ agreement, partyRole }, index) => toAgreementListItem(agreement, partyRole, index))
    .filter((row): row is AgreementListItem => !!row)

export const AGREEMENT_STATUS_FILTERS = ['all', 'draft', 'sent', 'signed', 'declined'] as const

export type AgreementStatusFilter = (typeof AGREEMENT_STATUS_FILTERS)[number]

const STATUS_KEY_BY_FILTER: Record<Exclude<AgreementStatusFilter, 'all'>, string> = {
  draft: AGREEMENT_STATUS_DRAFT,
  sent: AGREEMENT_STATUS_SENT,
  signed: AGREEMENT_STATUS_SIGNED,
  declined: AGREEMENT_STATUS_DECLINED,
}

export const groupAgreementListItems = (rows: AgreementListItem[]): AgreementStatusGroup[] => {
  const drafts = rows.filter(row => row.statusKey === AGREEMENT_STATUS_DRAFT)
  const sent = rows.filter(row => row.statusKey === AGREEMENT_STATUS_SENT)
  const signed = rows.filter(row => row.statusKey === AGREEMENT_STATUS_SIGNED)
  const declined = rows.filter(row => row.statusKey === AGREEMENT_STATUS_DECLINED)
  const other = rows.filter(row => !KNOWN_STATUS_KEYS.has(row.statusKey))

  return [
    { key: 'draft', label: 'Drafts', color: 'purple', rows: drafts },
    { key: 'sent', label: 'Pending Signature', color: 'yellow', rows: sent },
    { key: 'signed', label: 'Signed', color: 'green', rows: signed },
    { key: 'declined', label: 'Declined', color: 'red', rows: declined },
    { key: 'other', label: 'Other', color: 'gray', rows: other },
  ].filter(group => group.rows.length > 0)
}

export const rowsForAgreementStatusFilter = (
  rows: AgreementListItem[],
  filter: AgreementStatusFilter
): AgreementListItem[] => {
  if (filter === 'all') {
    return rows
  }
  return rows.filter(row => row.statusKey === STATUS_KEY_BY_FILTER[filter])
}

export const countAgreementStatusFilters = (
  rows: AgreementListItem[]
): Record<AgreementStatusFilter, number> => ({
  all: rows.length,
  draft: rows.filter(row => row.statusKey === AGREEMENT_STATUS_DRAFT).length,
  sent: rows.filter(row => row.statusKey === AGREEMENT_STATUS_SENT).length,
  signed: rows.filter(row => row.statusKey === AGREEMENT_STATUS_SIGNED).length,
  declined: rows.filter(row => row.statusKey === AGREEMENT_STATUS_DECLINED).length,
})

export const agreementPrimaryAction = (item: AgreementListItem): AgreementPrimaryAction => {
  if (item.statusKey === AGREEMENT_STATUS_SENT) {
    return { kind: 'sign', label: 'Sign' }
  }
  if (item.statusKey === AGREEMENT_STATUS_DRAFT && (item.canEdit || item.partyRole === 'FOUNDER')) {
    return { kind: 'edit', label: 'Edit' }
  }
  if (item.statusKey === AGREEMENT_STATUS_SIGNED) {
    return { kind: 'pdf', label: 'Download PDF' }
  }
  return { kind: 'view', label: 'View' }
}

/** Visible outline control to the left of primary. PDF unless that is already the CTA. */
export const agreementSecondaryAction = (item: AgreementListItem): AgreementPrimaryAction | null => {
  if (agreementPrimaryAction(item).kind === 'pdf') {
    return null
  }
  return { kind: 'pdf', label: 'PDF' }
}

export const agreementMenuGroups = (item: AgreementListItem): AgreementMenuGroup[] => {
  const groups: AgreementMenuGroup[] = []

  const document: AgreementMenuItem[] = []
  if (item.statusKey === AGREEMENT_STATUS_SENT && item.documensoDocumentId) {
    document.push({ kind: 'checkStatus', label: 'Check status' })
  }
  if (document.length > 0) {
    groups.push({ key: 'document', label: 'Document', items: document })
  }

  if (item.statusKey === AGREEMENT_STATUS_SENT && item.partyRole === 'ADVISOR') {
    groups.push({
      key: 'respond',
      label: 'Respond',
      items: [
        { kind: 'requestChanges', label: 'Request changes' },
        { kind: 'decline', label: 'Decline' },
      ],
    })
  }

  if (
    canDeleteUnsignedAdvisorAgreement({
      currentUserRole: item.partyRole === 'FOUNDER' ? 'founder' : 'advisor',
      statusKey: item.statusKey,
      currentUserCanDelete: item.canDelete,
    })
  ) {
    groups.push({
      key: 'danger',
      label: 'Remove',
      items: [{ kind: 'delete', label: 'Delete' }],
    })
  }

  return groups
}

export const agreementHasMenu = (item: AgreementListItem): boolean =>
  agreementMenuGroups(item).some(group => group.items.length > 0)

export const listHasBothPartyRoles = (rows: AgreementListItem[]): boolean =>
  rows.some(row => row.partyRole === 'FOUNDER') && rows.some(row => row.partyRole === 'ADVISOR')
